<template>
  <div class="modal fade" id="coverageConfigModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bx bx-cog me-2"></i>
            {{ selectedPlan ? `Configure Thresholds for ${selectedPlan.plan_name}` : 'System Coverage Configuration' }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="text-muted mt-2">Loading configurations...</p>
          </div>

          <div v-else>
            <!-- System Configurations -->
            <div class="mb-4">
              <h6 class="text-muted mb-3">
                <i class="bx bx-world me-1"></i>
                {{ selectedPlan ? 'Plan-Specific Configuration' : 'System-Wide Configuration' }}
              </h6>

              <div class="row g-3">
                <div class="col-md-6" v-for="(config, key) in localConfigs" :key="key">
                  <div class="card" :class="{ 'border-info': config.is_override, 'border-2': config.is_override }">
                    <div class="card-body">
                      <label class="form-label fw-bold">
                        {{ formatConfigLabel(key) }}
                        <span v-if="config.is_override" class="badge bg-info ms-2">
                          <i class="bx bx-customize me-1"></i>Override
                        </span>
                      </label>
                      <div class="input-group mb-2">
                        <span class="input-group-text" v-if="config.type === 'threshold' || config.type === 'amount'">
                          ETB
                        </span>
                        <span class="input-group-text" v-else-if="config.type === 'percentage'">
                          %
                        </span>
                        <span class="input-group-text" v-else-if="config.type === 'boolean'">
                          <i class="bx bx-toggle-left"></i>
                        </span>

                        <!-- Different input types based on config type -->
                        <select v-if="config.type === 'boolean'"
                                class="form-select"
                                v-model.number="config.value"
                                :disabled="!canEdit || selectedPlan">
                          <option :value="1">Enabled</option>
                          <option :value="0">Disabled</option>
                        </select>

                        <input v-else
                               type="number"
                               class="form-control"
                               v-model.number="config.value"
                               :step="config.type === 'percentage' ? '1' : '0.01'"
                               :min="config.min_value || 0"
                               :max="config.max_value"
                               :disabled="!canEdit || selectedPlan">
                      </div>

                      <small class="text-muted d-block">{{ config.description }}</small>

                      <div v-if="config.min_value !== null || config.max_value !== null" class="mt-1">
                        <small class="text-info">
                          Range: {{ config.min_value || 0 }} - {{ config.max_value || 'unlimited' }}
                          <span v-if="config.type === 'percentage'">%</span>
                          <span v-else-if="config.type === 'amount' || config.type === 'threshold'">ETB</span>
                        </small>
                      </div>

                      <div v-if="config.is_override && config.override_details" class="mt-2">
                        <small class="text-warning">
                          <i class="bx bx-info-circle"></i>
                          Override active from {{ formatDate(config.override_details.effective_from) }}
                          <span v-if="config.override_details.effective_to">
                            to {{ formatDate(config.override_details.effective_to) }}
                          </span>
                          <br>
                          <span class="text-muted">Reason: {{ config.override_details.reason }}</span>
                        </small>
                      </div>

                      <div v-if="!selectedPlan && config.default_value !== config.value" class="mt-1">
                        <small class="text-success">
                          <i class="bx bx-check"></i>
                          Modified (Default: {{ formatValue(config.default_value, config.type) }})
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No configurations message -->
              <div v-if="Object.keys(localConfigs).length === 0" class="alert alert-warning">
                <i class="bx bx-info-circle me-2"></i>
                No configurations found. Please initialize system configurations first.
                <button class="btn btn-sm btn-warning ms-3" @click="initializeConfigs">
                  <i class="bx bx-cog me-1"></i>Initialize Now
                </button>
              </div>
            </div>

            <!-- Plan Override Section (only if plan selected) -->
            <div v-if="selectedPlan && Object.keys(localConfigs).length > 0" class="mb-4">
              <div class="card border-primary">
                <div class="card-header bg-primary bg-opacity-10">
                  <h6 class="mb-0">
                    <i class="bx bx-customize me-1"></i>
                    Create or Update Plan Override
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label required">Configuration to Override</label>
                      <select class="form-select" v-model="overrideForm.configKey" @change="onConfigKeyChange">
                        <option value="">Select configuration</option>
                        <option v-for="(config, key) in localConfigs" :key="key" :value="key">
                          {{ formatConfigLabel(key) }}
                        </option>
                      </select>
                      <small class="text-warning d-block mt-1" v-if="getOverrideStatus()">
                        <i class="bx bx-info-circle"></i>
                        {{ getOverrideStatus() }}
                      </small>
                    </div>

                    <div class="col-md-6">
                      <label class="form-label required">Override Value</label>
                      <div class="input-group">
                        <span class="input-group-text" v-if="getSelectedConfigType() === 'threshold' || getSelectedConfigType() === 'amount'">
                          ETB
                        </span>
                        <span class="input-group-text" v-else-if="getSelectedConfigType() === 'percentage'">
                          %
                        </span>

                        <select v-if="getSelectedConfigType() === 'boolean'"
                                class="form-select"
                                v-model.number="overrideForm.value">
                          <option :value="1">Enabled</option>
                          <option :value="0">Disabled</option>
                        </select>

                        <input v-else
                               type="number"
                               class="form-control"
                               v-model.number="overrideForm.value"
                               :step="getSelectedConfigType() === 'percentage' ? '1' : '0.01'"
                               :min="0"
                               :placeholder="getCurrentValue()">
                      </div>
                      <small class="text-muted d-block mt-1" v-if="overrideForm.configKey">
                        Current value: {{ formatValue(getCurrentValue(), getSelectedConfigType()) }}
                        <span v-if="isOverridden()" class="text-info">(Override)</span>
                        <span v-else class="text-secondary">(System Default)</span>
                      </small>
                    </div>

                    <div class="col-md-6">
                      <label class="form-label required">Effective From</label>
                      <input type="date"
                             class="form-control"
                             v-model="overrideForm.effectiveFrom"
                             :min="getCurrentDate()">
                      <small class="text-muted">When this override becomes active</small>
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Effective To (Optional)</label>
                      <input type="date"
                             class="form-control"
                             v-model="overrideForm.effectiveTo"
                             :min="overrideForm.effectiveFrom || getCurrentDate()">
                      <small class="text-muted">Leave blank for indefinite</small>
                    </div>

                    <div class="col-12">
                      <label class="form-label required">Reason for Override</label>
                      <textarea class="form-control"
                                rows="2"
                                v-model="overrideForm.reason"
                                placeholder="Explain why this override is needed (e.g., special promotion, pilot program, etc.)"></textarea>
                    </div>

                    <div class="col-12">
                      <div class="d-flex gap-2">
                        <button class="btn btn-primary"
                                @click="createOverride"
                                :disabled="!canCreateOverride || savingOverride">
                          <span v-if="savingOverride" class="spinner-border spinner-border-sm me-2"></span>
                          <i v-else class="bx bx-plus-circle me-1"></i>
                          {{ savingOverride ? 'Creating...' : (isUpdatingExisting ? 'Update Override' : 'Create Override') }}
                        </button>

                        <button class="btn btn-outline-secondary"
                                @click="resetOverrideForm"
                                :disabled="savingOverride">
                          <i class="bx bx-refresh me-1"></i>
                          Reset Form
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Threshold Examples -->
            <div class="alert alert-info" v-if="Object.keys(localConfigs).length > 0">
              <h6 class="alert-heading">
                <i class="bx bx-info-circle me-1"></i>
                How Coverage Thresholds Work
              </h6>
              <hr>
              <ul class="mb-0">
                <li class="mb-2">
                  <strong>Mandatory Provider Threshold:</strong>
                  Below <strong>ETB {{ formatCurrency(localConfigs.mandatory_provider_threshold?.value || 15000) }}</strong>,
                  providers must deliver service regardless of patient's payment ability
                </li>
                <li class="mb-2">
                  <strong>Full Coverage Upper Limit:</strong>
                  Between ETB {{ formatCurrency(localConfigs.mandatory_provider_threshold?.value || 15000) }}
                  and <strong>ETB {{ formatCurrency(localConfigs.full_coverage_upper_limit?.value || 250000) }}</strong>,
                  CBHI covers at <strong>{{ localConfigs.default_coverage_percentage?.value || 100 }}%</strong>
                </li>
                <li class="mb-2">
                  <strong>Above Coverage Limit:</strong>
                  Above ETB {{ formatCurrency(localConfigs.full_coverage_upper_limit?.value || 250000) }},
                  <span v-if="(localConfigs.above_limit_topup_enabled?.value == 1)">
                    CBHI provides a <strong>{{ localConfigs.above_limit_topup_percentage?.value || 10 }}%</strong> top-up
                    (up to ETB {{ formatCurrency(localConfigs.above_limit_topup_max?.value || 50000) }})
                  </span>
                  <span v-else>
                    patient pays the full amount above the limit
                  </span>
                </li>
                <li>
                  <strong>Pre-Authorization Required:</strong>
                  Claims above <strong>ETB {{ formatCurrency(localConfigs.preauth_required_above_threshold?.value || 100000) }}</strong>
                  require pre-authorization
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i>Close
          </button>

          <button type="button"
                  class="btn btn-primary"
                  @click="saveConfigurations"
                  :disabled="saving || !hasChanges"
                  v-if="!selectedPlan && Object.keys(localConfigs).length > 0">
            <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bx bx-save me-1"></i>
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "CoverageConfigModal",
  props: {
    configs: {
      type: Object,
      default: () => ({})
    },
    selectedPlan: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      savingOverride: false,
      localConfigs: {},
      originalConfigs: {},
      overrideForm: {
        configKey: '',
        value: 0,
        effectiveFrom: '',
        effectiveTo: '',
        reason: ''
      },
      canEdit: true,
      isUpdatingExisting: false
    };
  },
  computed: {
    hasChanges() {
      return JSON.stringify(this.localConfigs) !== JSON.stringify(this.originalConfigs);
    },

    canCreateOverride() {
      return this.overrideForm.configKey &&
          this.overrideForm.value !== null &&
          this.overrideForm.value !== '' &&
          this.overrideForm.effectiveFrom &&
          this.overrideForm.reason &&
          this.overrideForm.reason.trim().length > 0;
    }
  },
  methods: {
    async openModal() {
      this.loading = true;
      this.resetOverrideForm();

      // Create modal instance
      const modal = new bootstrap.Modal(document.getElementById('coverageConfigModal'));
      modal.show();

      // Load configurations from API
      await this.loadConfigurations();
    },

    async loadConfigurations() {
      const data = {
        "function": "getCoverageConfigurations",
        ...(this.selectedPlan && { planId: this.selectedPlan.plan_id })
      };

      try {
        const res = await executeJson(data, authUrl);
        console.log('Loaded configurations:', res.data);

        if (res.data.success && res.data.data.configurations) {
          this.localConfigs = JSON.parse(JSON.stringify(res.data.data.configurations));
          this.originalConfigs = JSON.parse(JSON.stringify(res.data.data.configurations));
        } else {
          console.warn('No configurations found in response');
          this.localConfigs = {};
          this.originalConfigs = {};
        }
      } catch (e) {
        console.error("Failed to load configurations:", e);
        this.localConfigs = {};
        this.originalConfigs = {};
      } finally {
        this.loading = false;
      }
    },

    async initializeConfigs() {
      this.loading = true;

      const data = {
        "function": "initializeSystemConfigs"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          await this.loadConfigurations();
          this.$emit('config-updated', {
            success: true,
            message: 'System configurations initialized successfully'
          });
        }
      } catch (e) {
        console.error("Failed to initialize configs:", e);
      } finally {
        this.loading = false;
      }
    },

    async saveConfigurations() {
      this.saving = true;

      try {
        const updates = [];

        // Find changed configurations
        for (const [key, config] of Object.entries(this.localConfigs)) {
          if (config.value !== this.originalConfigs[key].value) {
            updates.push({
              key: key,
              value: config.value,
              description: config.description
            });
          }
        }

        if (updates.length > 0) {
          for (const update of updates) {
            const data = {
              "function": "updateCoverageConfig",
              "configKey": update.key,
              "configValue": update.value,
              "description": update.description
            };

            await executeJson(data, authUrl);
          }

          this.$emit('config-updated', {
            success: true,
            message: `${updates.length} configuration(s) updated successfully`
          });

          // Reload to get fresh data
          await this.loadConfigurations();
        }

        // Close the modal
        const modal = bootstrap.Modal.getInstance(document.getElementById('coverageConfigModal'));
        if (modal) {
          modal.hide();
        }
      } catch (e) {
        this.$emit('config-updated', {
          success: false,
          message: e.message || 'Failed to save configurations'
        });
      } finally {
        this.saving = false;
      }
    },

    async createOverride() {
      if (!this.canCreateOverride) return;

      this.savingOverride = true;

      const data = {
        "function": "createPlanThresholdOverride",
        "planId": this.selectedPlan.plan_id,
        "configKey": this.overrideForm.configKey,
        "overrideValue": this.overrideForm.value,
        "effectiveFrom": this.overrideForm.effectiveFrom,
        "effectiveTo": this.overrideForm.effectiveTo || null,
        "reason": this.overrideForm.reason
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          // Emit success event
          this.$emit('config-updated', {
            success: true,
            message: res.data.message || 'Override created successfully'
          });

          // Reset the form
          this.resetOverrideForm();

          // Reload configurations to show the new override
          await this.loadConfigurations();

          // Close the modal
          const modal = bootstrap.Modal.getInstance(document.getElementById('coverageConfigModal'));
          if (modal) {
            modal.hide();
          }
        } else {
          // Show error but don't close modal
          alert(res.data.message || 'Failed to create override');
        }
      } catch (e) {
        console.error('Failed to create override:', e);
        alert(e.message || 'Failed to create override');
      } finally {
        this.savingOverride = false;
      }
    },

    onConfigKeyChange() {
      if (this.overrideForm.configKey) {
        const config = this.localConfigs[this.overrideForm.configKey];
        if (config) {
          // Set default value to current value
          this.overrideForm.value = config.value;

          // Check if updating existing override
          this.isUpdatingExisting = config.is_override;
        }
      }
    },

    resetOverrideForm() {
      this.overrideForm = {
        configKey: '',
        value: 0,
        effectiveFrom: this.getCurrentDate(),
        effectiveTo: '',
        reason: ''
      };
      this.isUpdatingExisting = false;
    },

    formatConfigLabel(key) {
      if (!key) return '';
      return key
          .replace(/_/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase());
    },

    getSelectedConfigType() {
      if (!this.overrideForm.configKey || !this.localConfigs[this.overrideForm.configKey]) {
        return 'threshold';
      }
      return this.localConfigs[this.overrideForm.configKey].type;
    },

    getCurrentValue() {
      if (!this.overrideForm.configKey || !this.localConfigs[this.overrideForm.configKey]) {
        return 0;
      }
      return this.localConfigs[this.overrideForm.configKey].value;
    },

    isOverridden() {
      if (!this.overrideForm.configKey || !this.localConfigs[this.overrideForm.configKey]) {
        return false;
      }
      return this.localConfigs[this.overrideForm.configKey].is_override;
    },

    getOverrideStatus() {
      if (!this.overrideForm.configKey || !this.localConfigs[this.overrideForm.configKey]) {
        return '';
      }
      const config = this.localConfigs[this.overrideForm.configKey];
      if (config.is_override && config.override_details) {
        const fromDate = this.formatDate(config.override_details.effective_from);
        const toDate = config.override_details.effective_to ?
            ` to ${this.formatDate(config.override_details.effective_to)}` : '';
        return `Override exists from ${fromDate}${toDate}. Creating a new override will update it.`;
      }
      return '';
    },

    formatValue(value, type) {
      if (value === null || value === undefined) return 'N/A';

      if (type === 'percentage') {
        return `${value}%`;
      } else if (type === 'boolean') {
        return value == 1 ? 'Enabled' : 'Disabled';
      } else if (type === 'threshold' || type === 'amount') {
        return `ETB ${this.formatCurrency(value)}`;
      } else {
        return value;
      }
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    getCurrentDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }
  },

  watch: {
    selectedPlan: {
      handler(newVal) {
        if (newVal) {
          this.resetOverrideForm();
        }
      },
      immediate: true
    }
  },

  beforeUnmount() {
    // Clean up any open modals
    const modalEl = document.getElementById('coverageConfigModal');
    if (modalEl) {
      const modal = bootstrap.Modal.getInstance(modalEl);
      if (modal) {
        modal.dispose();
      }
    }
  }
};
</script>

<style scoped>
.card {
  transition: all 0.3s ease;
}

.card.border-info {
  border-color: #0dcaf0 !important;
}

.card.border-2 {
  border-width: 2px !important;
}

.card.border-primary {
  border-color: #0d6efd !important;
}

.bg-opacity-10 {
  opacity: 0.1;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.form-label.required::after {
  content: " *";
  color: red;
}

.alert-heading {
  font-size: 1rem;
  font-weight: 600;
}

.modal-dialog {
  max-width: 800px;
}

.input-group-text {
  min-width: 50px;
  justify-content: center;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.text-muted {
  font-size: 0.875rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}
</style>